import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Calendar,
    Clock,
    MapPin,
    Users,
    DollarSign,
    ExternalLink,
    Star,
    Share2,
    ArrowLeft,
    Music,
    Video,
    Info,
    Heart,
    MessageCircle,
    Tag,
    Globe,
    Facebook
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatAgeRequirement } from '../utils/eventLabels';
import { profileThumb, profileThumbFallback } from '../utils/imageHelpers';
import { fetchLinkPreview } from '../utils/linkPreview';
import ShareModal from '../components/ShareModal';
import ContributingArtistsDisplay from '../components/ContributingArtistsDisplay';

const EventDetails = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRSVPed, setIsRSVPed] = useState(false);
    const [rsvpLoading, setRsvpLoading] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [shareModal, setShareModal] = useState(false);
    const [fbPreview, setFbPreview] = useState(null);
    const [fbPreviewLoading, setFbPreviewLoading] = useState(false);

    useEffect(() => {
        fetchEventDetails();
    }, [eventId]);

    useEffect(() => {
        if (!event?.facebook_event_url) {
            setFbPreview(null);
            return;
        }
        setFbPreviewLoading(true);
        fetchLinkPreview(event.facebook_event_url)
            .then(setFbPreview)
            .catch(() => setFbPreview(null))
            .finally(() => setFbPreviewLoading(false));
    }, [event?.facebook_event_url]);

    const fetchEventDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.get(`/events/${eventId}/public`);
            
            if (response.data.success) {
                setEvent(response.data.data);
                // Check if user is already RSVP'd (you might want to add this to the backend)
                // setIsRSVPed(response.data.data.user_rsvp_status === 'going');
            } else {
                setError('Event not found');
            }
        } catch (err) {
            console.error('Error fetching event details:', err);
            if (err.response?.status === 404) {
                setError('Event not found');
            } else {
                setError('Failed to load event details');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRSVP = async (status = 'going') => {
        if (!user) {
            navigate('/login');
            return;
        }

        try {
            setRsvpLoading(true);

            // Must use the resolved numeric id here, not the route param — the
            // URL (and so eventId) may hold the slug instead of the numeric id.
            const response = await api.post(`/events/${event.id}/rsvp`, { status });
            
            if (response.data.success) {
                setIsRSVPed(status === 'going');
                // Optionally refetch event to update attendee count
                fetchEventDetails();
            }
        } catch (err) {
            console.error('Error updating RSVP:', err);
        } finally {
            setRsvpLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            weekday: 'long',
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true
        });
    };

    const getEventTypeColor = (type) => {
        const colors = {
            'concert': 'text-purple-400 bg-purple-500/20 border-purple-500/30',
            'festival': 'text-pink-400 bg-pink-500/20 border-pink-500/30',
            'livestream': 'text-blue-400 bg-blue-500/20 border-blue-500/30',
            'workshop': 'text-green-400 bg-green-500/20 border-green-500/30',
            'meetup': 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
            'release': 'text-red-400 bg-red-500/20 border-red-500/30',
            'other': 'text-gray-400 bg-gray-500/20 border-gray-500/30'
        };
        return colors[type] || colors.other;
    };

    const getStatusColor = (status) => {
        const colors = {
            'published': 'text-green-400 bg-green-500/20 border-green-500/30',
            'draft': 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
            'cancelled': 'text-red-400 bg-red-500/20 border-red-500/30',
            'postponed': 'text-orange-400 bg-orange-500/20 border-orange-500/30',
            'completed': 'text-blue-400 bg-blue-500/20 border-blue-500/30'
        };
        return colors[status] || 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    };

    const getEventShareText = () => {
        const eventTimes = event.end_date
            ? `${formatTime(event.start_date)} - ${formatTime(event.end_date)}`
            : formatTime(event.start_date);

        return `Join The Deep Wave for ${event.title} on ${formatDate(event.start_date)} from ${eventTimes} hope to see you there!`;
    };

    const shareEvent = () => {
        setShareModal(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-6 pb-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="glass-card p-8">
                            <div className="h-12 bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-4 bg-gray-700 rounded w-1/2 mb-6"></div>
                            <div className="space-y-3">
                                <div className="h-4 bg-gray-700 rounded"></div>
                                <div className="h-4 bg-gray-700 rounded w-4/5"></div>
                                <div className="h-4 bg-gray-700 rounded w-3/5"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 pt-6 pb-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="glass-card p-8 text-center">
                        <div className="text-6xl mb-4">🎫</div>
                        <h1 className="text-2xl font-bold text-white mb-4">{error}</h1>
                        <p className="text-gray-400 mb-6">
                            {error === 'Event not found' 
                                ? "This event might have been removed or doesn't exist." 
                                : "Please try again later."
                            }
                        </p>
                        <button 
                            onClick={() => navigate(-1)}
                            className="btn-primary"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!event) return null;

    return (
        <div className="min-h-screen bg-gray-900 pt-6 pb-12">
            <div className="max-w-4xl mx-auto px-6">
                {/* Back Button */}
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 transition-colors mb-6 group"
                >
                    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    Back to Events
                </button>

                {/* Event Header */}
                <div
                    className="glass-card relative overflow-hidden mb-8"
                    style={{
                        backgroundImage: event.cover_image_url ? `url(${event.cover_image_url})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                >
                    {/* Dark overlay for text readability over the cover image */}
                    {event.cover_image_url && (
                        <div className="absolute inset-0 bg-black/50"></div>
                    )}

                    <div className="relative z-10 p-8">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-4">
                                <span className={`px-3 py-1 rounded-full text-sm border ${getEventTypeColor(event.event_type)}`}>
                                    {event.event_type}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-sm border ${getStatusColor(event.status)}`}>
                                    {event.status}
                                </span>
                                {!!event.is_featured && (
                                    <div className="flex items-center gap-1 text-yellow-400 text-sm bg-black/70 px-3 py-1 rounded-full">
                                        <Star className="w-4 h-4 fill-current" />
                                        Featured
                                    </div>
                                )}
                            </div>

                            <h1 className="inline-block text-4xl font-black mb-4 bg-black/70 px-3 py-1 rounded-lg">
                                <span className="gradient-text">{event.title}</span>
                            </h1>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
                                <div className="flex items-center gap-3 bg-black/70 rounded-lg p-3 border border-green-500/40">
                                    <Calendar className="w-5 h-5 text-green-400" />
                                    <div>
                                        <div className="font-extrabold text-green-400">{formatDate(event.start_date)}<span className="italic font-black text-green-300">!</span></div>
                                        {event.end_date && formatDate(event.end_date) !== formatDate(event.start_date) && (
                                            <div className="text-sm text-gray-300">
                                                Ends: {formatDate(event.end_date)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 bg-black/70 rounded-lg p-3 border border-blue-500/40">
                                    <Clock className="w-5 h-5 text-blue-400" />
                                    <div>
                                        <div className="font-extrabold text-blue-400">{formatTime(event.start_date)}</div>
                                        {event.end_date && (
                                            <div className="text-sm text-gray-300">
                                                Ends: {formatTime(event.end_date)}
                                            </div>
                                        )}
                                        <div className="text-sm text-gray-300">{event.timezone}</div>
                                    </div>
                                </div>

                                {(event.venue_name || event.city) && (
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                            `${event.venue_name || ''} ${event.address || ''} ${[event.city, event.state, event.country].filter(Boolean).join(', ')}`
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 bg-black/70 rounded-lg p-3 hover:text-yellow-300 transition-colors group"
                                        title="Open in Google Maps"
                                    >
                                        <MapPin className="w-5 h-5 text-yellow-400 group-hover:text-yellow-300 group-hover:scale-110 transition-all" />
                                        <div>
                                            {event.venue_name && (
                                                <div className="font-medium group-hover:underline">{event.venue_name}</div>
                                            )}
                                            <div className="text-sm text-gray-300 group-hover:text-yellow-200 group-hover:underline">
                                                {[event.city, event.state, event.country].filter(Boolean).join(', ')}
                                            </div>
                                        </div>
                                    </a>
                                )}

                                {event.capacity && (
                                    <div className="flex items-center gap-3 bg-black/70 rounded-lg p-3">
                                        <Users className="w-5 h-5 text-yellow-400" />
                                        <div>
                                            <div className="font-medium">{event.capacity.toLocaleString()} capacity</div>
                                            {event.attendee_count > 0 && (
                                                <div className="text-sm text-gray-300">
                                                    {event.attendee_count} attending
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-3 lg:w-64">
                            {event.ticket_price && (
                                <div className="text-center p-4 bg-black/70 rounded-lg border border-green-500/40">
                                    <div className="flex items-center justify-center gap-2 text-green-400 text-2xl font-bold">
                                        <DollarSign className="w-6 h-6" />
                                        {event.ticket_price}
                                    </div>
                                    <div className="text-sm text-gray-300 mt-1">
                                        {formatAgeRequirement(event.age_requirement)}
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2">
                                {user && (
                                    <button
                                        onClick={() => handleRSVP(isRSVPed ? 'not_going' : 'going')}
                                        disabled={rsvpLoading}
                                        className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                                            isRSVPed
                                                ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-600/30'
                                                : 'btn-primary'
                                        }`}
                                    >
                                        {rsvpLoading ? (
                                            <div className="loading-spinner w-5 h-5 mx-auto"></div>
                                        ) : (
                                            <>
                                                <Heart className={`w-5 h-5 inline mr-2 ${isRSVPed ? 'fill-current' : ''}`} />
                                                {isRSVPed ? 'Going' : 'RSVP'}
                                            </>
                                        )}
                                    </button>
                                )}

                                <button
                                    onClick={shareEvent}
                                    className="px-4 py-3 bg-gray-800/70 text-gray-300 hover:text-yellow-400 hover:bg-gray-800/90 rounded-lg transition-all border border-gray-600/50"
                                    title="Share"
                                >
                                    <Share2 className="w-5 h-5" />
                                </button>
                            </div>

                            {event.ticket_url && (
                                <a
                                    href={event.ticket_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-secondary flex items-center justify-center gap-2"
                                >
                                    <ExternalLink className="w-5 h-5" />
                                    Get Tickets
                                </a>
                            )}
                        </div>
                    </div>
                    </div>
                </div>

                {/* Event Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        {/* Description */}
                        {event.description && (
                            <div className="glass-card p-6 mb-8">
                                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                    <Info className="w-5 h-5 text-yellow-400" />
                                    About This Event
                                </h2>
                                <div className="prose prose-invert prose-purple max-w-none">
                                    <div className="whitespace-pre-line text-gray-300 leading-relaxed">
                                        {event.description}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Address */}
                        {event.address && (
                            <div className="glass-card p-6 mb-8">
                                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                            `${event.venue_name || ''} ${event.address} ${[event.city, event.state, event.country].filter(Boolean).join(', ')}`
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 hover:text-yellow-300 transition-colors group"
                                        title="Open in Google Maps"
                                    >
                                        <MapPin className="w-5 h-5 text-yellow-400 group-hover:text-yellow-300 group-hover:scale-110 transition-all" />
                                        Location
                                    </a>
                                </h2>
                                <div className="text-gray-300">
                                    {event.venue_name && (
                                        <div className="font-medium text-white mb-2">{event.venue_name}</div>
                                    )}
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                            `${event.venue_name || ''} ${event.address} ${[event.city, event.state, event.country].filter(Boolean).join(', ')}`
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block hover:text-yellow-300 transition-colors cursor-pointer group"
                                        title="Open in Google Maps"
                                    >
                                        <div className="group-hover:underline">{event.address}</div>
                                        <div className="group-hover:underline">
                                            {[event.city, event.state, event.country].filter(Boolean).join(', ')}
                                        </div>
                                        <div className="text-sm text-gray-500 group-hover:text-yellow-400 mt-1 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            Click to view in Google Maps
                                        </div>
                                    </a>
                                </div>
                            </div>
                        )}

                        {/* Tags */}
                        {event.tags && event.tags.length > 0 && (
                            <div className="glass-card p-6">
                                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                    <Tag className="w-5 h-5 text-yellow-400" />
                                    Tags
                                </h2>
                                <div className="flex flex-wrap gap-2">
                                    {event.tags.map((tag, index) => (
                                        <span 
                                            key={index}
                                            className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm border border-purple-500/30"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Media */}
                        {(event.cover_image_url || event.promotional_video_url) && (
                            <div className="glass-card p-6">
                                <h3 className="text-lg font-bold text-white mb-4">Media</h3>

                                {event.cover_image_url && (
                                    <div className="mb-4">
                                        <img
                                            src={event.cover_image_url}
                                            alt={event.title}
                                            className="w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                            onClick={() => setShowImageModal(true)}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}

                                {event.promotional_video_url && (
                                    <a 
                                        href={event.promotional_video_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors"
                                    >
                                        <Video className="w-4 h-4" />
                                        Watch Promotional Video
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Event Details */}
                        <div className="glass-card p-6">
                            <h3 className="text-lg font-bold text-white mb-4">Event Details</h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Type:</span>
                                    <span className="text-white capitalize">{event.event_type}</span>
                                </div>
                                
                                {event.age_requirement && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Age:</span>
                                        <span className="text-white">{formatAgeRequirement(event.age_requirement)}</span>
                                    </div>
                                )}

                                <div className="flex justify-between">
                                    <span className="text-gray-400">Status:</span>
                                    <span className="text-white capitalize">{event.status}</span>
                                </div>

                                {event.timezone && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Timezone:</span>
                                        <span className="text-white">{event.timezone}</span>
                                    </div>
                                )}

                                <div className="flex justify-between">
                                    <span className="text-gray-400">Visibility:</span>
                                    <span className="text-white flex items-center gap-1">
                                        <Globe className="w-3 h-3" />
                                        {event.is_public ? 'Public' : 'Private'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Facebook Event Link */}
                        {event.facebook_event_url && (
                            <div className="glass-card p-6">
                                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                    <Facebook className="w-5 h-5 text-blue-500" />
                                    Facebook Event
                                </h3>
                                <a
                                    href={event.facebook_event_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block rounded-lg overflow-hidden border border-gray-700 hover:border-blue-500/50 transition-colors"
                                >
                                    {fbPreviewLoading ? (
                                        <div className="p-6 text-center text-gray-500 text-sm">Loading preview...</div>
                                    ) : (() => {
                                        // Ignore linkPreview's generic site-branding guess (isFallback) —
                                        // our own event data is a better fallback than "TheDeepWave".
                                        const realPreview = fbPreview && !fbPreview.isFallback ? fbPreview : null;
                                        const previewDescription = realPreview?.description || event.description;
                                        return (
                                            <>
                                                {realPreview?.image && (
                                                    <img
                                                        src={realPreview.image}
                                                        alt={realPreview.title || 'Facebook event preview'}
                                                        className="w-full aspect-video object-cover"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                )}
                                                <div className="p-4 bg-gray-800/50">
                                                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                                        {realPreview?.site_name || 'facebook.com'}
                                                    </div>
                                                    <div className="font-semibold text-white mb-1 line-clamp-2">
                                                        {realPreview?.title || event.title}
                                                    </div>
                                                    {previewDescription && (
                                                        <p className="text-sm text-gray-400 line-clamp-2">
                                                            {previewDescription}
                                                        </p>
                                                    )}
                                                    <div className="mt-3 flex items-center gap-1 text-blue-400 text-sm font-medium">
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                        View Event on Facebook
                                                    </div>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </a>
                            </div>
                        )}

                        {/* Organizer Info */}
                        {event.creator_name && (
                            <div className="glass-card p-6">
                                <h3 className="text-lg font-bold text-white mb-4">Organizer</h3>
                                {event.creator_username ? (
                                    <Link to={`/profile/${event.creator_username}`} className="flex items-center gap-3 group">
                                        {event.creator_avatar ? (
                                            <img
                                                src={profileThumb(event.creator_avatar)}
                                                alt={event.creator_name}
                                                width="40"
                                                height="40"
                                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                onError={profileThumbFallback(event.creator_avatar)}
                                            />
                                        ) : (
                                            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-white font-bold">
                                                    {event.creator_name?.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        )}
                                        <div>
                                            <div className="font-medium text-white group-hover:text-purple-400 transition-colors">{event.creator_name}</div>
                                            <div className="text-sm text-gray-400">@{event.creator_username}</div>
                                        </div>
                                    </Link>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        {event.creator_avatar ? (
                                            <img
                                                src={profileThumb(event.creator_avatar)}
                                                alt={event.creator_name}
                                                width="40"
                                                height="40"
                                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                onError={profileThumbFallback(event.creator_avatar)}
                                            />
                                        ) : (
                                            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-white font-bold">
                                                    {event.creator_name?.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        )}
                                        <div className="font-medium text-white">{event.creator_name}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Contributing Artists */}
                        <ContributingArtistsDisplay
                            mediaType="event"
                            mediaId={event.id}
                            className="glass-card p-6"
                            layout="vertical"
                        />
                    </div>
                </div>
            </div>

            {/* Full-size flyer image modal */}
            {showImageModal && event.cover_image_url && (
                <div
                    className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setShowImageModal(false)}
                >
                    <button
                        onClick={() => setShowImageModal(false)}
                        aria-label="Close image"
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gray-800/80 hover:bg-gray-700 flex items-center justify-center text-white transition-colors"
                    >
                        ✕
                    </button>
                    <img
                        src={event.cover_image_url}
                        alt={event.title}
                        className="max-w-full max-h-full object-contain rounded-lg"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            <ShareModal
                isOpen={shareModal}
                onClose={() => setShareModal(false)}
                mediaTitle={event.title}
                mediaType="event"
                mediaUrl={window.location.href}
                description={event.description}
                imageUrl={event.cover_image_url}
                shareText={getEventShareText()}
            />
        </div>
    );
};

export default EventDetails;